import { first, maxBy } from 'lodash'
import { z } from 'zod'

import { countries, FlagCountry, isSupportedCountry } from '@forto-dev/phoenix-ui-core'

import { getZodEnumFromObjectKeys } from '@/utils/getZodEnumFromObjectKeys'

import { getDoorLocationCountryCode, getDoorLocationLabel } from './getCargoDoorLocations'
import { getFullAddress } from './getFullAddress'
import { HouseShipment, HouseShipmentNode, HouseShipmentTransportPoint } from '../domain/houseShipment'
import { ResolvedAddressMap, TimelineHouseCargo } from '../types'

export const DateStatusSchema = z.enum(['estimated', 'actual'])
export type DateStatus = z.infer<typeof DateStatusSchema>
export const TimelineMilestoneDataSchema = z.object({
  dateStatus: DateStatusSchema,
  location: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  progress: z.number(),
  delayDays: z
    .number()
    .nullish()
    .transform((data) => data ?? undefined),
  date: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined), // TODO: why is this nullable instead of optional?
  // Pre-carriage collection timestamp, only populated for the origin milestone.
  pickupDate: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  pickupIsActual: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  flag: getZodEnumFromObjectKeys(countries)
    .nullish()
    .transform((data) => data ?? undefined),
  isHandlingByThirdParty: z
    .boolean()
    .nullish()
    .transform((data) => data ?? undefined),
  outgoingConnections: z
    .array(
      z.object({
        houseCargoIDs: z.array(z.string()),
        modeOfTransport: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        targetNodeID: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        vendor: z
          .object({
            accountID: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
            legalEntityID: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
            partnerCode: z
              .string()
              .nullish()
              .transform((data) => data ?? undefined),
          })
          .nullish()
          .transform((data) => data ?? undefined),
        vesselName: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
        flightNumber: z
          .string()
          .nullish()
          .transform((data) => data ?? undefined),
      })
    )
    .nullish()
    .transform((data) => data ?? []),
})
export type TimelineMilestoneData = z.infer<typeof TimelineMilestoneDataSchema>

export const TranshipmentDataSchema = z.object({
  total: z.number(),
  completed: z.number(),
  data: z.array(
    z.object({
      port: z.string(),
      modeOfTransport: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      departure: z.coerce
        .date()
        .nullish()
        .transform((data) => data ?? undefined),
      arrival: z.coerce
        .date()
        .nullish()
        .transform((data) => data ?? undefined),
      vesselName: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      flightNumber: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      carrierId: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      dateStatus: DateStatusSchema,
    })
  ),
})
export type TranshipmentData = z.infer<typeof TranshipmentDataSchema>

export const TimelineSchema = z.object({
  origin: TimelineMilestoneDataSchema,
  departure: TimelineMilestoneDataSchema,
  arrival: TimelineMilestoneDataSchema,
  delivery: TimelineMilestoneDataSchema,
  transhipment: TranshipmentDataSchema.nullish().transform((data) => data ?? undefined),
  // Shipment-level facts the rows and labels branch on, so they never depend on a node connection.
  transportMode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  transportType: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  airline: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  flightNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})
export type Timeline = z.infer<typeof TimelineSchema>

export const defaultTimelineMilestone: TimelineMilestoneData = {
  dateStatus: 'estimated',
  progress: 0,
  date: undefined,
  location: '—',
  outgoingConnections: [],
}

type NodeTypes =
  | 'place-of-pickup'
  | 'place-of-delivery'
  | 'port-of-loading'
  | 'port-of-discharge'
  | 'transshipment-port'

export const timelineMilestoneKeyToNodeType: Record<TimelineMilestoneKey, NodeTypes> = {
  origin: 'place-of-pickup',
  delivery: 'place-of-delivery',
  departure: 'port-of-loading',
  arrival: 'port-of-discharge',
  transhipment: 'transshipment-port',
} as const

export const getHouseCargoesForTimeline = (
  shipment: HouseShipment,
  resolvedLocations: ResolvedAddressMap = new Map()
): TimelineHouseCargo[] => {
  const nodesPerCargoIds: { [key: string]: string[] } = {}
  shipment.nodes.forEach((node) => {
    node.outgoingConnections?.forEach((connection) => {
      connection.houseCargoIDs?.forEach((cargoId) => {
        if (!nodesPerCargoIds[cargoId]) {
          nodesPerCargoIds[cargoId] = []
        }
        if (connection.targetNodeID) {
          nodesPerCargoIds[cargoId].push(connection.targetNodeID)
        }
      })
    })
  })
  const houseCargosWithLocation = shipment.houseCargos
    ? shipment.houseCargos.map((houseCargo) => {
        const cargoId = houseCargo.ID
        const cargoNodesIds = nodesPerCargoIds[cargoId]
        const cargoNodes = shipment.nodes.filter((node) => cargoNodesIds?.includes(node.id))

        const originTransportPoint = cargoNodes.find((node) => node.type === 'place-of-pickup')?.transportPoint
        const departureTransportPoint = cargoNodes.find((node) => node.type === 'port-of-loading')?.transportPoint
        const arrivalTransportPoint = cargoNodes.find((node) => node.type === 'port-of-discharge')?.transportPoint
        const deliveryTransportPoint = cargoNodes.find((node) => node.type === 'place-of-delivery')?.transportPoint

        const originAddress = getDoorLocationLabel(originTransportPoint, resolvedLocations)
        const departureAddress = departureTransportPoint && getAddressLocation(departureTransportPoint)
        const arrivalAddress = arrivalTransportPoint && getAddressLocation(arrivalTransportPoint)
        const deliveryAddress = getDoorLocationLabel(deliveryTransportPoint, resolvedLocations)

        return { ...houseCargo, originAddress, departureAddress, arrivalAddress, deliveryAddress }
      })
    : []

  return houseCargosWithLocation
}

export const timelineMilestoneKeys = ['origin', 'departure', 'transhipment', 'arrival', 'delivery'] as const
export type TimelineMilestoneKey = (typeof timelineMilestoneKeys)[number]

const isEndpointNode = (node?: HouseShipmentNode) => {
  return node?.type === 'place-of-pickup' || node?.type === 'place-of-delivery'
}

const isTSMilestone = (milestoneKey?: TimelineMilestoneKey) => {
  return milestoneKey !== undefined && milestoneKey === 'transhipment'
}

export const getTimelineData = (shipment: HouseShipment, resolvedLocations: ResolvedAddressMap = new Map()) => {
  const milestones: Timeline = {
    origin: { ...defaultTimelineMilestone },
    departure: { ...defaultTimelineMilestone },
    arrival: { ...defaultTimelineMilestone },
    delivery: { ...defaultTimelineMilestone },
  }

  let isCascadedActual = false

  // Iterate over the milestones in reverse order to ensure that the actual milestones are processed first
  // This is necessary to correctly calculate the cascaded actual flag
  for (let i = timelineMilestoneKeys.length - 1; i >= 0; i--) {
    const milestoneKey = timelineMilestoneKeys[i]

    const nodeType = timelineMilestoneKeyToNodeType[milestoneKey]
    const nodes = shipment.nodes.filter((node) => node.type === nodeType)

    const isActual = isActualNode(nodes[0]) || isCascadedActual

    if (isTSMilestone(milestoneKey)) {
      isCascadedActual = processTranshipmentMilestones(milestones, shipment, isCascadedActual)
      continue
    }

    processMilestones(milestones, milestoneKey, nodes, shipment, isActual, isCascadedActual, resolvedLocations)

    if (isActual) {
      isCascadedActual = true
    }
  }

  // Deliberately the milestone's own isActual, not the cascaded flag: a downstream actual must not
  // make an unconfirmed pickup read as "Picked Up".
  const pickupMilestone = getPlaceOfPickupNode(shipment)?.milestones.timeOfDeparture
  milestones.origin.pickupDate = pickupMilestone?.timeStampUTC
  milestones.origin.pickupIsActual = pickupMilestone?.isActual

  milestones.transportMode = shipment.details.mainTransportMode
  milestones.transportType = shipment.details.mainTransportType
  milestones.airline = shipment.details.airline
  milestones.flightNumber = shipment.details.flightNumber

  return milestones
}

const processTranshipmentMilestones = (milestones: Timeline, shipment: HouseShipment, isCascadedActual: boolean) => {
  const tsNodes = shipment.nodes.filter((node) => node.type === 'transshipment-port')
  if (tsNodes.length === 0) {
    return isCascadedActual
  }

  // Get the carrier ID from the first node since all sea nodes have the same carrier
  const carrierId = first(first(shipment.nodes)?.outgoingConnections)?.vendor?.accountID

  milestones.transhipment = {
    total: 0,
    completed: 0,
    data: [],
  }

  tsNodes.forEach((node) => {
    const isActual = isActualNode(node) || isCascadedActual

    if (milestones.transhipment) {
      milestones.transhipment.total += 1
      milestones.transhipment.completed += isActual ? 1 : 0
      milestones.transhipment.data.push({
        port: getLocation(node.transportPoint),
        departure: getDepartureNodeDate(node),
        arrival: getArrivalNodeDate(node),
        vesselName: first(node?.outgoingConnections)?.vesselName,
        flightNumber: first(node?.outgoingConnections)?.flightNumber,
        carrierId: carrierId,
        dateStatus: getDateStatus(isActual),
        modeOfTransport: first(node?.outgoingConnections)?.modeOfTransport,
      })
    }

    if (isActual) {
      isCascadedActual = true
    }
  })

  return isCascadedActual
}

const processMilestones = (
  milestones: Timeline,
  milestoneKey: Exclude<TimelineMilestoneKey, 'transhipment'>,
  nodes: HouseShipmentNode[] | undefined,
  shipment: HouseShipment,
  isActual: boolean,
  isCascadedActual: boolean,
  resolvedLocations: ResolvedAddressMap
) => {
  const locationGetter = isEndpointNode(nodes?.[0])
    ? (transportPoint: HouseShipmentTransportPoint) => getDoorLocationLabel(transportPoint, resolvedLocations) ?? ''
    : getLocation
  const locations = nodes
    ? nodes.map((node) => (node ? locationGetter(node.transportPoint) : milestones[milestoneKey].location))
    : []
  let location: string | undefined
  const countries = nodes && nodes.map((node) => getCountry(node.transportPoint))

  if (locations.length > 1 && !locations.every((loc) => loc === locations[0]) && countries) {
    location = [...new Set(countries)].join(', ')
  } else {
    location = first(locations)
  }

  const date = getNodeDate(milestoneKey, shipment, nodes?.[0])
  milestones[milestoneKey] = {
    ...milestones[milestoneKey],
    progress: getPercentage({
      node: nodes?.[0],
      milestoneKey,
      shipment,
      isCascadedActual,
    }),
    dateStatus: getDateStatus(isActual),
    date: date ? date : milestones[milestoneKey].date,
    delayDays: nodes?.[0] ? getNodeDelayDays(nodes?.[0]) : milestones[milestoneKey].delayDays,
    location,
    flag: nodes?.[0] ? getFlag(nodes?.[0].transportPoint, resolvedLocations) : milestones[milestoneKey].flag,
    outgoingConnections: nodes?.[0] ? nodes?.[0].outgoingConnections : [],
  }
}

const getLocationString = (data: { name: string; ID: string }): string => {
  return `${data.name} (${data.ID})`
}

const getLocation = (transportPoint: HouseShipmentTransportPoint): string => {
  if (transportPoint.port) {
    return getLocationString(transportPoint.port)
  }
  if (transportPoint.depot) {
    return getLocationString(transportPoint.depot)
  }
  if (transportPoint.terminal) {
    return getLocationString(transportPoint.terminal)
  }
  if (transportPoint.address) {
    return getFullAddress(transportPoint.address)
  }
  return ''
}

const getAddressLocation = (transportPoint: HouseShipmentTransportPoint): string => {
  if (transportPoint.address) {
    return getFullAddress(transportPoint.address)
  }

  return ''
}

const getCountry = (transportPoint: HouseShipmentTransportPoint): string | undefined => {
  if (transportPoint.address?.countryCode) {
    return transportPoint.address.country
  }

  if (transportPoint.port?.countryCode) {
    return transportPoint.port.countryCode
  }

  return undefined
}

const getFlag = (
  transportPoint: HouseShipmentTransportPoint,
  resolvedLocations: ResolvedAddressMap
): FlagCountry | undefined => {
  if (transportPoint.port?.countryCode && isSupportedCountry(transportPoint.port.countryCode.toUpperCase())) {
    return transportPoint.port.countryCode.toUpperCase() as FlagCountry
  }
  const doorCountryCode = getDoorLocationCountryCode(transportPoint, resolvedLocations)
  if (doorCountryCode && isSupportedCountry(doorCountryCode.toUpperCase())) {
    return doorCountryCode.toUpperCase() as FlagCountry
  }
  return undefined
}

const getDepartureNodeDate = (node?: HouseShipmentNode): Date | undefined => {
  return node?.milestones.timeOfDeparture?.timeStampUTC
}

const getArrivalNodeDate = (node?: HouseShipmentNode): Date | undefined => {
  return node?.milestones.timeOfArrival?.timeStampUTC
}

// Shipment-level `details.cargoReadyDate` is stale after a per-container CRD edit in TMS, so derive
// it from the fresh per-container values (latest wins), falling back to the shipment-level value.
export const getShipmentCargoReadyDate = (shipment: HouseShipment): Date | undefined => {
  const containerDates = (shipment.houseCargos ?? [])
    .map((cargo) => cargo.milestones?.cargoReadyDate?.date)
    .filter((date): date is Date => date != null)

  const latest = maxBy(containerDates, (date) => date.getTime())

  return latest ?? shipment.details.cargoReadyDate?.timeStampUTC
}

const getNodeDate = (
  milestoneKey: Exclude<TimelineMilestoneKey, 'transhipment'>,
  shipment: HouseShipment,
  node?: HouseShipmentNode
): Date | undefined => {
  if (milestoneKey === 'origin') {
    return getShipmentCargoReadyDate(shipment)
  }

  if (milestoneKey === 'departure') {
    return node?.milestones.timeOfDeparture?.timeStampUTC
  }

  return node?.milestones.timeOfArrival?.timeStampUTC
}

const isActualNode = (node?: HouseShipmentNode): boolean => {
  if (node?.type === 'port-of-loading') {
    return Boolean(node?.milestones.timeOfDeparture?.isActual)
  }

  return Boolean(node?.milestones.timeOfArrival?.isActual || node?.milestones.timeOfDeparture?.isActual)
}

export const getDateStatus = (isActual: boolean): DateStatus => {
  if (isActual) {
    return 'actual'
  }

  return 'estimated'
}

export const getPercentage = ({
  node,
  isCascadedActual,
  milestoneKey,
  shipment,
}: {
  node?: HouseShipmentNode
  isCascadedActual?: boolean
  milestoneKey: Exclude<TimelineMilestoneKey, 'transhipment'>
  shipment: HouseShipment
}): number => {
  if (isCascadedActual) {
    return 100
  }

  if (!node?.milestones.timeOfDeparture?.isActual) {
    return 0
  }

  let nextNode: HouseShipmentNode | undefined
  if (timelineMilestoneKeys.includes(milestoneKey)) {
    let nextMilestoneKey = timelineMilestoneKeys[timelineMilestoneKeys.indexOf(milestoneKey) + 1]
    if (nextMilestoneKey === 'transhipment') {
      nextMilestoneKey = timelineMilestoneKeys[timelineMilestoneKeys.indexOf(nextMilestoneKey) + 1]
    }
    nextNode = shipment.nodes.find((node) => node.type === timelineMilestoneKeyToNodeType[nextMilestoneKey])
  }

  if (!nextNode) {
    return 0
  }

  const currentTime = Date.now()
  const startTime = getTime(getNodeDate(milestoneKey, shipment, node))
  const endTime = getTime(getNodeDate(milestoneKey, shipment, nextNode))

  return startTime && endTime ? Math.min(90, (100 * (currentTime - startTime)) / (endTime - startTime)) : 0
}

export const getTime = (date?: Date): number | null => {
  return date ? new Date(date).getTime() : null
}

export const getPlaceOfDeliveryNode = (shipment: HouseShipment): HouseShipmentNode | undefined => {
  return shipment.nodes.find((node) => node.type === 'place-of-delivery')
}

export const getPlaceOfPickupNode = (shipment: HouseShipment): HouseShipmentNode | undefined => {
  return shipment.nodes.find((node) => node.type === 'place-of-pickup')
}

const getNodeDelayDays = (node?: HouseShipmentNode): number | undefined => {
  if (!node) {
    return undefined
  }
  return node.delayInDays > 0 ? node.delayInDays : undefined
}

export type ShipmentDelayBasis = 'delivery' | 'arrival'

export type ShipmentDelay = { delayDays: number; basis?: ShipmentDelayBasis }

export const getShipmentDelay = (shipment: HouseShipment): ShipmentDelay => {
  return {
    delayDays: shipment.customerShipmentStatus?.delayInDays ?? 0,
    basis: shipment.customerShipmentStatus?.delayBasis,
  }
}
